import React, { useRef, useMemo, useState } from 'react';
import { Drawer, notification, Select, Spin, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { cloneDeep, debounce, trim, filter } from 'lodash';
import moment from 'moment';

import AgGrid from '../../../../../../../components/ag-grid-table/index';
import Input from '../../../../../../../components/uiComponent/inputs/input/index';
import SelectInput from '../../../../../../../components/uiComponent/inputs/selectableInput';
import Button from '../../../../../../../components/uiComponent/button/index';
import LeftArrow from '../../../../../../../assets/icons/arrow-left-blue.svg';
import BarCodeScanner from '../../../../../../../assets/icons/bar-code-scan-icon.svg';
import ProductImage from '../../../../../../../assets/icons/product-image.svg';

import { CreateMultiSKUBoxWrapper } from '../../style';
import { CreateMultiSkuBox, GetWarehouseShipments, GetPackingItems } from '../../../../../../../slices/post-shipment';
import mix2DLablePdf from '../../../../../../../actions/utils/mix2DLablePdf';
import getBoxIndex from '../../../../../../../utils/get-box-index';
import box2DLabels from '../../../../../../../utils/print-labels/box2DLabels';
import { buildPackNowLabelItems, formatBoxCode, isValidFnsku, notifyMissingFnsku } from './packNowLabelHelpers';

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true
};

let gridApi;

const MultiSkuDrawer = ({
  setCreateMultiSKU,
  isCreateMultiSKU,
  toBeBoxed,
  activeShipmentIndex,
  boxesList,
  packingGroupId,
  packingMethod,
}) => {
  const { workOrderId } = useParams();
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  const [filteredItems, setFilteredItems] = useState(toBeBoxed);
  const [groupFilter, setGroupFilter] = useState(null);
  const isPackNow = packingMethod === 'pack_now';
  const [itemsDetail, setItemsDetail] = useState(toBeBoxed?.map(({ id: shipment_item_id, sku_id, fnsku }) => ({ shipment_item_id, pack_quantity: 0, is_selected: false, sku_id, fnsku })) || []);
  const [dimensions, setDimensions] = useState({
    height: 5,
    width: 5,
    length: 5,
    weight: 5,
    unit: 'lb'
  });

  const {
    creatingMultiSkuBox,
    shipmentDetails,
    skuGroupMap,
  } = useSelector((state) => state.postShipment || {});

  const groupLabelMap = useMemo(() => {
    const map = {};
    const ids = [...new Set(Object.values(skuGroupMap || {}))].filter(Boolean).sort();
    ids.forEach((id, i) => { map[id] = `Group ${i + 1}`; });
    return map;
  }, [skuGroupMap]);

  const handleGroupFilter = (groupId) => {
    setGroupFilter(groupId || null);
    const source = groupId ? toBeBoxed.filter(item => skuGroupMap?.[item.msku] === groupId) : toBeBoxed;
    setFilteredItems(source.map(entry => ({ ...entry, pack_quantity: itemsDetail.find(i => i.shipment_item_id === entry.id)?.pack_quantity || 0 })));
    onSelectionChange();
  };

  const {
    amazon_shipment_id: amazonShipmentId,
    amazon_shipment_confirmation_id: shipmentId,
    name: shipmentName,

    amazon_warehouse_id: FCID,
    destination_name,
    destination_city,
    destination_postal_code,
    destination_address_line_1,
    destination_state_or_province_code,

    source_city,
    source_country_code,
    source_postal_code,
    source_name,
    source_address_line_1
  } = shipmentDetails?.shipments?.[activeShipmentIndex] || {};

  const handleInput = ({ e, shipment_item_id, availableQuantity }) => {
    if (!e.target.value) {
      setItemsDetail(itemsDetail => {
        const itemsDetailClone = cloneDeep(itemsDetail);

        const index = itemsDetailClone.findIndex(item => item.shipment_item_id === shipment_item_id);
        itemsDetailClone[index].pack_quantity = 0;
        itemsDetailClone[index].bundleQuantities = [];
        return itemsDetailClone;
      });
      return;
    }
    const quantity = Number(e.target.value);

    if (!quantity || isNaN(quantity) || (quantity < 0)) {
      return notification.warning({
        message: 'Please enter a valid pack quantity!',
        top: 60
      });
    }

    if (availableQuantity < quantity) {
      return notification.error({
        message: 'Pack quantity should be less than or equal to available quantity',
        top: 65
      });
    }

    setItemsDetail(itemsDetail => {
      const itemsDetailClone = cloneDeep(itemsDetail);

      const index = itemsDetailClone.findIndex(item => item.shipment_item_id === shipment_item_id);
      itemsDetailClone[index].pack_quantity = quantity;
      itemsDetailClone[index].is_selected = true;

      return itemsDetailClone;
    });

    onSelectionChange();
  };

  const gridReady = (params) => {
    gridApi = params.api;
    onSelectionChange();
  };

  const columnDefs = useMemo(() => [
    {
      width: 35,
      headerCheckboxSelection: true,
      checkboxSelection: true
    },
    {
      headerName: 'Title',
      sortable: true,
      flex: 1,
      filter: true,
      cellRenderer: ({ data }) => {
        const { image_url, item_name } = data || {};

        return (
          <div className='title-ui-wrapper'>
            <img src={image_url || ProductImage} alt="Product-image" />
            <Tooltip placement='top' title={item_name || ''}>
              <p>
                {item_name || '---'}
              </p>
            </Tooltip>
          </div>
        );
      }
    },
    {
      headerName: 'SKU',
      field: 'msku',
      sortable: true,
      flex: 1,
      filter: true
    },
    {
      headerName: 'FNSKU',
      field: 'fnsku',
      sortable: true,
      flex: 1,
      filter: true
    },
    {
      headerName: 'Packing Group',
      sortable: true,
      filter: true,
      width: 130,
      hide: !isPackNow,
      cellRenderer: ({ data }) => {
        const { msku, expiration } = data || {};
        const groupId = skuGroupMap?.[msku];
        const label = groupLabelMap[groupId] || (groupId ? 'Group 1' : null);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {label ? (
              <Tooltip title={`Amazon Packing Group: ${groupId}`}>
                <span style={{ fontSize: '11px', color: '#1565D8', fontWeight: 600 }}>{label}</span>
              </Tooltip>
            ) : <span style={{ color: '#aaa', fontSize: '11px' }}>—</span>}
            {expiration && (
              <span style={{ fontSize: '10px', color: '#d48806' }}>Exp: {expiration}</span>
            )}
          </div>
        );
      }
    },
    {
      headerName: 'Available Qty',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: ({ data }) => {
        const { quantity, boxed_quantity = 0 } = data || {};
        return (Number(quantity) - boxed_quantity);
      }
    },
    {
      headerName: 'Box Qty',
      sortable: true,
      filter: true,
      flex: 1,
      field: 'pack_quantity',
      cellRenderer: ({ data, value }) => {
        const { quantity, boxed_quantity = 0, id: shipment_item_id, product_details = [] } = data || {};
        const availableQuantity = Number(quantity) - boxed_quantity;
        const bundleQuantities = (product_details || []).map(item => item.bundle_quantity);
        let packQty = 0;
        setItemsDetail(itemsDetail => {
          bundleQuantities,
            packQty = itemsDetail.find(item => item.shipment_item_id === shipment_item_id)?.pack_quantity;
          return itemsDetail;
        });

        return (
          <div className='input-wrapper'>
            <Input
              placeholder="Enter"
              type="number"
              defaultValue={value || packQty || ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInput({ e, shipment_item_id, availableQuantity });
              }}
              onBlur={(e) => handleInput({ e, shipment_item_id, availableQuantity })}
            />
          </div>
        );
      }
    }
  ], [filteredItems, isPackNow]);

  const handleGenerateMix2DBoxLabel = (boxesList, boxIndex) => {
    const items = itemsDetail
    .filter(({ pack_quantity }) => pack_quantity)
    .map(({ shipment_item_id, pack_quantity }) => {
      const { fnsku, expiration } = toBeBoxed.find(({ id }) => id === shipment_item_id) || {};
  
      let exp = null;
      if (expiration) {
        exp = moment(expiration, 'YYYY-MM-DD').format('YYMMDD');
      }
  
      return { fnsku, qty: pack_quantity, exp };
    });

    const missingFnsku = itemsDetail
      .filter(({ pack_quantity }) => pack_quantity)
      .map(({ shipment_item_id }) => toBeBoxed.find(({ id }) => id === shipment_item_id) || {})
      .filter(({ fnsku }) => !isValidFnsku(fnsku))
      .map(({ msku }) => msku || '—');
    if (missingFnsku.length) {
      notifyMissingFnsku(missingFnsku.join(', '));
      return;
    }

    const ship = {
      warehouse_name: source_name,
      address_line1: source_address_line_1,
      city: source_city,
      state_or_province_code: source_postal_code,
      country_code: source_country_code,

      ship_to_name: destination_name,
      destination_fulfillment_center_id: FCID,
      ship_to_address1: destination_address_line_1,
      ship_to_city: destination_city,
      ship_to_state: destination_state_or_province_code,
      ship_to_postal_code: destination_postal_code
    };

    mix2DLablePdf({
      shipmentId,
      userName: 'Opulent',
      shipmentName,
      dateCreated: moment().format('DD/MM/YY'),
      outbound_shipment_id: amazonShipmentId,
      items,
      numberOfBox: 1,
      PageStartIndex: 0,
      is2D: false,
      ship,
      boxIndex: boxIndex === -1 ? (boxesList?.length + 1) : (boxIndex + 1) // if box index is -1 then it means new box is created
    });
  };

  const handleGeneratePackNowBoxLabel = ({ packingBoxes = [], boxId }) => {
    const boxIndex = packingBoxes.findIndex(box => String(box.id) === String(boxId));
    const boxNumber = boxIndex === -1 ? packingBoxes.length : boxIndex + 1;
    const box = packingBoxes[boxIndex] || {};
    const { items = [], box_code, weight, length, height, width, unit, created_at } = box;
    const mappedItems = buildPackNowLabelItems({ boxItems: items, packingItems: toBeBoxed });

    if (!mappedItems.length) return;

    const enrichedItems = mappedItems.map((mi) => {
      const packingItem = toBeBoxed?.find(pi => String(pi.sku_id || pi.id) === String(mi.sku_id)) || {};
      return { ...mi, name: packingItem.item_name || packingItem.name || '', msku: packingItem.msku || packingItem.sku || '' };
    });

    const missingFnsku = enrichedItems.filter(mi => !isValidFnsku(mi.fnsku)).map(mi => mi.msku || '—');
    if (missingFnsku.length) {
      notifyMissingFnsku(missingFnsku.join(', '));
      return;
    }

    box2DLabels({
      isPackNow: true,
      boxCode: formatBoxCode(box_code, box.id),
      boxNumber,
      items: enrichedItems,
      dimensions: { length, width, height, unit },
      weight,
      createdAt: created_at,
    });
  };

  const handleCreateMultiSkuBox = () => {
    const isPackNow = packingMethod === 'pack_now';

    if (isPackNow) {
      const selected = itemsDetail.filter(({ is_selected }) => is_selected);
      const selectedItems = selected.map(({ shipment_item_id, sku_id }) =>
        toBeBoxed.find(i => i.id === shipment_item_id || i.sku_id === sku_id) || {}
      );

      // Check packing group conflict — Amazon forbids packing SKUs from different groups together
      if (Object.keys(skuGroupMap || {}).length) {
        const groups = [...new Set(selectedItems.map(i => skuGroupMap[i.msku]).filter(Boolean))];
        if (groups.length > 1) {
          notification.error({
            message: 'These SKUs cannot be packed together. Amazon assigned them to different packing groups.',
            description: `Group 1: ...${groups[0].slice(-8)}  |  Group 2: ...${groups[1].slice(-8)}`,
            top: 65,
            duration: 8,
          });
          return;
        }
      }

      // Check expiry mixing
      const hasExpiry = selectedItems.some(i => !!i.expiration);
      const hasNoExpiry = selectedItems.some(i => !i.expiration);
      if (hasExpiry && hasNoExpiry) {
        notification.error({
          message: 'Cannot mix expiry-dated and non-expiry items in the same box.',
          top: 65,
          duration: 6,
        });
        return;
      }
    }

    const payload = {
      itemsDetail: itemsDetail?.filter(({ is_selected }) => is_selected)?.map(({ shipment_item_id, pack_quantity, sku_id, fnsku }) => ({
        shipment_item_id,
        pack_quantity,
        sku_id,
        fnsku
      })),
      workOrderId: Number(workOrderId),
      ...(isPackNow ? {
        dimensions: {
          ...dimensions,
          weight: {
            value: dimensions?.weight,
            unit: dimensions?.unit || 'LB'
          }
        },
        packingGroupId: (() => {
          const selected = itemsDetail.filter(({ is_selected }) => is_selected);
          const firstMsku = toBeBoxed.find(i => i.id === selected[0]?.shipment_item_id || i.sku_id === selected[0]?.sku_id)?.msku;
          return (skuGroupMap && firstMsku && skuGroupMap[firstMsku]) || packingGroupId;
        })()
      } : {
        amazonShipmentId,
        dimensions: {
          ...dimensions,
          weight: {
            value: dimensions?.weight,
            unit: dimensions?.unit || 'LB'
          }
        }
      })
    };

    dispatch(CreateMultiSkuBox(payload))
      .then(({ payload }) => {
        if (payload?.success) {
          setCreateMultiSKU(false);
          const isPackNow = packingMethod === 'pack_now';
          const refreshAction = isPackNow
            ? GetPackingItems({ workOrderId })
            : GetWarehouseShipments({ workOrderId });
            
          dispatch(refreshAction).then(({ payload: refreshPayload }) => {
            const boxId = payload?.boxId;
            const boxIndex = getBoxIndex([], boxId, shipmentDetails?.shipments);
            if (isPackNow) {
              handleGeneratePackNowBoxLabel({
                packingBoxes: refreshPayload?.packingBoxes || [],
                boxId
              });
            } else {
              handleGenerateMix2DBoxLabel(boxesList, boxIndex);
            }
          });
        }
      });
  };

  const baseItems = groupFilter
    ? toBeBoxed.filter(item => skuGroupMap?.[item.msku] === groupFilter)
    : toBeBoxed;



  const handleSearch = debounce(({ target }) => {
    const { value } = target || {};
    const searchTerm = trim(value)?.toLowerCase();
    
    if (searchTerm) {
      setFilteredItems(
        filter(baseItems, item => 
          item.fnsku?.toLowerCase().includes(searchTerm) || 
          item.msku?.toLowerCase().includes(searchTerm) ||
          item.asin?.toLowerCase().includes(searchTerm)
        ).map((entry) => ({ ...entry, pack_quantity: itemsDetail.find(item => item.shipment_item_id === entry.id)?.pack_quantity || 0 }))
      );
    }
    else {
      setFilteredItems(
        baseItems.map((entry) => ({ ...entry, pack_quantity: itemsDetail.find(item => item.shipment_item_id === entry.id)?.pack_quantity || 0 }))
      );
    }
    onSelectionChange();
  }, 300);

  const getCreateBtnDisabledStatus = () => {
    return (
      creatingMultiSkuBox || !itemsDetail?.filter(({ is_selected }) => is_selected).length ||
      itemsDetail.some(({ pack_quantity, is_selected }) => is_selected && !pack_quantity)
    );
  };

  const onSelectionChange = (e) => {
    if (!gridApi) return;
    if (!e) {
      setItemsDetail(itemsDetail => {
        itemsDetail?.forEach(({ shipment_item_id, is_selected }) => {
          const node = gridApi.getRowNode(shipment_item_id);
          if (node && is_selected) node.setSelected(true);
        });
        return itemsDetail;
      });
    }
    else {
      const selectedRows = gridApi.getSelectedRows();
      const selectedRowIds = selectedRows.map(({ id }) => id);
      const visibleItemIds = filteredItems.map(({ id }) => id);

      // Only update is_selected for items currently visible in the grid.
      // Items filtered out (by group filter or search) keep their previous is_selected,
      // so selections persist across filter switches and Create Box stays enabled.
      const data = itemsDetail?.map((obj) =>
        visibleItemIds.includes(obj.shipment_item_id)
          ? { ...obj, is_selected: selectedRowIds.includes(obj.shipment_item_id) }
          : obj
      );

      setItemsDetail(data);
    }
  };
  const { height, width, length, weight, unit } = dimensions || {};
  return (
    <Drawer
      placement="right"
      closable={false}
      width={996}
      onClose={() => setCreateMultiSKU(false)}
      visible={isCreateMultiSKU}
      bodyStyle={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <Spin tip='Loading...' wrapperClassName="loading-info-wrapper" spinning={creatingMultiSkuBox}>
        <CreateMultiSKUBoxWrapper style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
          <header style={{ padding: '16px 24px' }}>
            <div className='items-wrapper'>
              <img onClick={() => setCreateMultiSKU(false)} src={LeftArrow} alt="LeftArrow" />
              <p>Create Multi SKU Box</p>
            </div>
            <Input
              inputRef={inputRef}
              className="input-overlay-wrapper"
              placeholder="Barcode Scanner"
              endIcon={<img src={BarCodeScanner} alt="BarCodeScanner" />}
              onChange={handleSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch.cancel();
                  handleSearch({ target: e.target });
                }
              }}
            />
          </header>

          {/* Table area — fills remaining space; AgGrid scrolls internally */}
          <div style={{ flex: 1, overflow: 'hidden', padding: '16px 24px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <p className="title" style={{ margin: 0 }}>{"Item's Info."}</p>
              {isPackNow && Object.keys(groupLabelMap).length > 0 && (
                <Select
                  placeholder="Filter by Packing Group"
                  style={{ width: 200 }}
                  value={groupFilter || undefined}
                  onChange={handleGroupFilter}
                  allowClear
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                >
                  {Object.entries(groupLabelMap).map(([id, label]) => (
                    <Select.Option key={id} value={id}>{label}</Select.Option>
                  ))}
                </Select>
              )}
            </div>
            <AgGrid
              user_referrals={filteredItems}
              defaultColDef={defaultColDef}
              columnDefinitions={columnDefs}
              gridReady={gridReady}
              rowSelection='multiple'
              onSelectionChanged={onSelectionChange}
              rowHeight={64}
              headerHeight={36}
              // `height` on this component only gates its own auto-responsive height detection
              // (it doesn't account for this drawer's Add Dimension footer) - `containerHeight`
              // is what actually sizes the grid. Pack Now's taller footer (Add Dimension block +
              // button) needs more headroom subtracted than Pack Later's (button only), otherwise
              // the grid overflows past the footer and hides the last rows/scroll behind it.
              containerHeight={isPackNow ? 'calc(100vh - 420px)' : 'calc(100vh - 260px)'}
              className="sku-table"
            />
          </div>

          {/* Footer — pinned to bottom */}
          <div style={{ flexShrink: 0, padding: '16px 24px', borderTop: '1px solid #ECEFF1', background: '#fff' }}>
            {isPackNow && (
              <div style={{ padding: '16px', background: '#F8FAFB', borderRadius: '8px', marginBottom: '16px', border: '1px solid #ECEFF1' }}>
                <p style={{ fontWeight: 600, fontSize: '14px', color: '#212A2F', marginBottom: '16px' }}>Add Dimension</p>
                <div className='items-wrapper' style={{ display: 'flex', gap: '12px' }}>
                  <Input
                    className="dimension-input"
                    placeholder="Enter"
                    label="Length"
                    value={length}
                    onChange={({ target }) => setDimensions(d => ({ ...d, length: target.value }))}
                    endIcon="Inches"
                  />
                  <Input
                    className="dimension-input"
                    placeholder="Enter"
                    label="Height"
                    value={height}
                    onChange={({ target }) => setDimensions(d => ({ ...d, height: target.value }))}
                    endIcon="Inches"
                  />
                  <Input
                    className="dimension-input"
                    placeholder="Enter"
                    label="Width"
                    value={width}
                    onChange={({ target }) => setDimensions(d => ({ ...d, width: target.value }))}
                    endIcon="Inches"
                  />
                  <SelectInput
                    defaultDomain={weight}
                    defaultExtension={unit}
                    onDomainChange={value => setDimensions(d => ({ ...d, weight: value }))}
                    onExtensionChange={value => setDimensions(d => ({ ...d, unit: value }))}
                    extensions={['kg', 'lb']}
                    error={false}
                  />
                </div>
              </div>
            )}
            <Button
              onClick={handleCreateMultiSkuBox}
              text="Create Box & Print Box Label"
              disabled={getCreateBtnDisabledStatus()}
              style={{ width: '100%' }}
            />
          </div>
        </CreateMultiSKUBoxWrapper>
      </Spin>
    </Drawer>
  );
};

export default MultiSkuDrawer;
